/**
 * GSAP Slide Gallery Card Component
 * Displays a GSAP slide in gallery view with proper 16:9 scaling
 */

import React, { useEffect, useRef, useState } from 'react';
import { Card } from '../ui/card';
import { cn } from '../../lib/utils';
import { GSAP_VIEWER_CONFIG } from '../../config/gsapViewer';
import { UnifiedSlide } from '../../types/gsapSlide';
import { formatDuration } from '../../utils/slideTypeDetector';
import { slidesAPI } from '../../services/api';
import { useAgentJob } from '../../hooks/useAgentJob';
import { Play } from 'lucide-react';

interface SlideGenerationStatus {
  status: 'pending' | 'generating' | 'completed' | 'error';
  progress?: number;
  jobId?: string;
  error?: string;
  startedAt?: Date;
  completedAt?: Date;
}

interface GsapSlideCardProps {
  slide: UnifiedSlide;
  onSlideClick?: (slidePath: string) => void;
  onEditClick?: (slideId: string, sceneNumber: number) => void;
  onApplyTemplate?: (templateId: string) => Promise<void>;
  conversationId?: string;
  generationStatus?: SlideGenerationStatus;
  isApplying?: boolean;
  isApplied?: boolean;
  applyButtonLabel?: string;
  index?: number;
}

export const GsapSlideCard: React.FC<GsapSlideCardProps> = ({
  slide,
  onSlideClick,
  onEditClick,
  onApplyTemplate,
  conversationId,
  generationStatus,
  isApplying = false,
  isApplied = false,
  applyButtonLabel = 'Apply Template',
  index = 0,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.15);
  const [shouldLoadIframe, setShouldLoadIframe] = useState(false);
  const [viewMode, setViewMode] = useState<'preview' | 'plan'>('preview');
  const [activeTab, setActiveTab] = useState<'overview' | 'script'>('overview');
  const [iframeError, setIframeError] = useState<string | null>(null);
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Get active job for thinking display
  const { job } = useAgentJob(generationStatus?.jobId);

  const isGenerating = generationStatus?.status === 'generating';

  // Lazy load iframe based on visibility
  useEffect(() => {
    // Load immediately if in first 3 slides
    if (index < 3) {
      setShouldLoadIframe(true);
      return;
    }

    // Set up intersection observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !shouldLoadIframe) {
            console.log(`GsapSlideCard - Lazy loading iframe for slide ${index}`);
            setShouldLoadIframe(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.01,
      }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index, shouldLoadIframe]);

  // Listen for postMessage errors from iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Only accept messages from our iframe
      if (event.data && event.data.type === 'viewer-error') {
        console.error(`GsapSlideCard - Iframe error for slide ${slide.number}:`, event.data.error);
        setIframeError(event.data.error);
      } else if (event.data && event.data.type === 'viewer-loaded') {
        console.log(`GsapSlideCard - Iframe loaded successfully for slide ${slide.number}`);
        setIframeLoaded(true);
        setIframeError(null);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [slide.number]);

  // Scale calculation effect - matches existing ExpandableSlideCard
  useEffect(() => {
    const updateScale = () => {
      if (containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect();
        const containerWidth = containerRect.width;
        const containerHeight = containerRect.height;

        const scaleX = containerWidth / GSAP_VIEWER_CONFIG.slideResolution.width;
        const scaleY = containerHeight / GSAP_VIEWER_CONFIG.slideResolution.height;
        const optimalScale = Math.min(scaleX, scaleY);

        setScale(optimalScale);
      }
    };

    updateScale();
    window.addEventListener('resize', updateScale);

    if (containerRef.current) {
      const resizeObserver = new ResizeObserver(updateScale);
      resizeObserver.observe(containerRef.current);

      return () => {
        window.removeEventListener('resize', updateScale);
        resizeObserver.disconnect();
      };
    }

    return () => window.removeEventListener('resize', updateScale);
  }, []);

  const handlePreviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onSlideClick) {
      onSlideClick(slide.path);
    }
  };

  const duration = slide.duration || slide.metadata?.estimated_duration || 0;
  const layoutType = slide.layoutType || slide.metadata?.scene_id;

  // Extract relative path from absolute slide path
  // slide.path is like: /mnt/filestore/.../slides/slide-001/slide.json
  // We need: slides/slide-001/slide.json
  const extractRelativePath = (absolutePath: string): string => {
    const slidesIndex = absolutePath.indexOf('/slides/');
    if (slidesIndex !== -1) {
      return absolutePath.substring(slidesIndex + 1); // Remove leading '/'
    }
    // Fallback: return the filename
    return absolutePath.split('/').slice(-3).join('/'); // Get last 3 segments
  };

  const relativePath = extractRelativePath(slide.path);

  // Build API URL for JSON file serving
  // Use slideJsonUrl if available (for templates), fallback to API endpoint
  const jsonApiUrl = (slide.metadata as any)?.slideJsonUrl
    || slidesAPI.getJSONUrl(conversationId || '', relativePath);

  // Build GSAP viewer URL with the API URL as the slide parameter
  const viewerUrl = GSAP_VIEWER_CONFIG.buildUrl(jsonApiUrl, {
    editMode: false,
    controls: false,  // Hide controls for clean gallery preview
    uiMode: 'external',  // Use external UI mode to show only slide content
    conversationId,
  });

  // Debug logging for gallery card
  console.log(`🎴 GsapSlideCard - Slide ${slide.number}:`, {
    slidePath: slide.path,
    relativePath,
    jsonApiUrl,
    viewerUrl,
    conversationId,
    shouldLoadIframe,
  });

  // Debug metadata to diagnose why plan view is empty
  console.log(`🔍 GsapSlideCard - Slide ${slide.number} METADATA:`, {
    hasMetadata: !!slide.metadata,
    metadata: slide.metadata,
    hasGsapData: !!slide.gsapData,
    gsapDataKeys: slide.gsapData ? Object.keys(slide.gsapData) : [],
    // Check root-level fields in gsapData
    rootLevelFields: slide.gsapData ? {
      script_text: slide.gsapData.script_text,
      content_description: slide.gsapData.content_description,
      scene_id: slide.gsapData.scene_id,
      layout_type: slide.gsapData.layout_type,
      visual_instructions: slide.gsapData.visual_instructions,
    } : null
  });

  // Handle iframe load
  const handleIframeLoad = () => {
    console.log(`✅ GsapSlideCard - Iframe loaded for slide ${slide.number}`);
    setIframeLoaded(true);
    setIframeError(null);
  };

  // Handle iframe error
  const handleIframeError = (error: any) => {
    console.error(`❌ GsapSlideCard - Iframe error for slide ${slide.number}:`, error);
    setIframeError('Failed to load slide viewer');
  };

  return (
    <Card
      ref={cardRef}
      className={cn(
        "group transition-all duration-300 hover:shadow-xl bg-card relative overflow-hidden h-[334px] flex flex-col rounded-xl backdrop-blur-sm",
        isApplied
          ? "border-2 border-primary shadow-md ring-2 ring-primary/20"
          : "border border-border/60 hover:border-primary/50"
      )}
    >
      {/* Toggle Bar */}
      <div className="flex-shrink-0 border-b bg-muted/30 overflow-hidden">
        <div className="flex">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setViewMode('preview');
            }}
            className={cn(
              'flex-1 py-2 px-3 text-xs font-medium transition-all flex items-center justify-center gap-1',
              viewMode === 'preview'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
              />
            </svg>
            <span>Preview</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setViewMode('plan');
            }}
            className={cn(
              'flex-1 py-2 px-3 text-xs font-medium transition-all flex items-center justify-center gap-1',
              viewMode === 'plan'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
            )}
          >
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <span>Plan</span>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden relative">
        {viewMode === 'preview' ? (
          /* Preview Content */
          <div className="h-full flex flex-col p-4 relative">
            {/* Simplified Header - Only Scene # and Title */}
            <div className="mb-3 flex-shrink-0">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-primary">Scene {slide.number}:</span>
                <h3 className="font-semibold text-sm text-foreground flex-1 truncate">
                  {slide.title}
                </h3>
              </div>
            </div>

            {/* Preview Section - Clickable for full screen */}
            <div
              className="relative flex-1 cursor-pointer rounded-lg overflow-hidden bg-gradient-to-br from-muted/20 to-muted/5"
              onClick={handlePreviewClick}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              title="Click to view full screen"
            >
              <div
                ref={containerRef}
                className="h-full overflow-hidden relative flex items-center justify-center"
              >
                {iframeError ? (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-red-50/50 p-4">
                    <svg className="w-8 h-8 text-red-500 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-xs text-red-700 text-center">{iframeError}</span>
                  </div>
                ) : shouldLoadIframe ? (
                  <>
                    <iframe
                      key={`gsap-iframe-${slide.id}`}
                      ref={iframeRef}
                      src={viewerUrl}
                      className="absolute border-0 pointer-events-none"
                      title={slide.title}
                      sandbox="allow-same-origin allow-scripts allow-forms allow-modals"
                      onLoad={handleIframeLoad}
                      onError={handleIframeError}
                      style={{
                        width: `${GSAP_VIEWER_CONFIG.slideResolution.width}px`,
                        height: `${GSAP_VIEWER_CONFIG.slideResolution.height}px`,
                        transform: `scale(${scale})`,
                        transformOrigin: 'top left',
                        left: '0',
                        top: '0',
                      }}
                    />
                    {!iframeLoaded && (
                      <div className="absolute inset-0 flex items-center justify-center bg-muted/10 pointer-events-none">
                        <div className="flex flex-col items-center gap-2">
                          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-xs text-muted-foreground">Loading slide...</span>
                        </div>
                      </div>
                    )}
                    {/* Play icon overlay on hover */}
                    {iframeLoaded && isHovered && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none transition-all duration-200">
                        <div className="bg-white/90 rounded-full p-4 shadow-lg">
                          <Play className="h-8 w-8 text-primary fill-primary" />
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted/10">
                    <span className="text-xs text-muted-foreground">Loading...</span>
                  </div>
                )}

                {/* Hover overlay with expand icon */}
                <div className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-all duration-300 flex items-center justify-center opacity-0 hover:opacity-100">
                  <div className="bg-white/95 backdrop-blur-md rounded-full p-3 shadow-lg">
                    <svg
                      className="w-6 h-6 text-gray-700"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Section - Thinking and Actions */}
            <div className="flex-shrink-0 space-y-2 mt-3">
              {/* Agent Thinking Display - shown when generating and thinking */}
              {isGenerating && job?.progress?.thinking && (
                <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400 bg-accent/50 p-2 rounded-md border border-primary/30">
                  <svg className="h-3 w-3 animate-spin flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span className="flex-1 leading-relaxed">{job.progress.thinking}</span>
                </div>
              )}

              {/* Action Row - Edit, Scene #, Duration */}
              <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/30">
                {/* Left: Action buttons */}
                <div className="flex items-center gap-1">
                  {onEditClick && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const slideId = slide.id || slide.path.split('/').slice(-2, -1)[0];
                        const sceneNumber = typeof slide.number === 'number' ? slide.number : parseInt(slide.number, 10);
                        onEditClick(slideId, sceneNumber);
                      }}
                      className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary hover:bg-accent rounded-md transition-colors"
                      title="Edit this slide (small targeted changes)"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span>Edit</span>
                    </button>
                  )}
                  {onApplyTemplate && slide.metadata?.isTemplate && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const templateId = slide.id || slide.metadata?.templateId;
                        if (templateId) {
                          onApplyTemplate(templateId);
                        }
                      }}
                      disabled={isApplying || isApplied}
                      className={cn(
                        "flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md transition-colors",
                        isApplied
                          ? "text-green-600 bg-green-50 cursor-default"
                          : "text-primary hover:bg-primary/10",
                        "disabled:opacity-50 disabled:cursor-not-allowed"
                      )}
                      title={isApplied ? "This template is currently applied" : "Apply this template to your project"}
                    >
                      {isApplying ? (
                        <>
                          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span>Applying...</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d={isApplied ? "M5 13l4 4L19 7" : "M12 4v16m8-8H4"}
                            />
                          </svg>
                          <span>{applyButtonLabel}</span>
                        </>
                      )}
                    </button>
                  )}
                </div>

                {/* Right: Info badges */}
                <div className="flex items-center gap-2">
                  {/* Generating status badge */}
                  {isGenerating && (
                    <span className="bg-accent text-accent-foreground text-[10px] font-medium px-1.5 py-0.5 rounded-full border border-primary/30 flex items-center gap-1">
                      <svg className="h-3 w-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span className="text-[10px]">Generating</span>
                    </span>
                  )}
                  {/* Layout type badge */}
                  {layoutType && (
                    <span className="bg-accent text-accent-foreground text-[10px] font-medium px-1.5 py-0.5 rounded-full capitalize">
                      {layoutType.replace('layout_', '').replace(/_/g, ' ')}
                    </span>
                  )}
                  {/* Duration badge */}
                  {duration > 0 && (
                    <div className="bg-muted text-muted-foreground text-[10px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-1">
                      <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      {formatDuration(duration)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Plan View Content - Match PlanOnlySlideCard styling */
          <div className="h-full flex flex-col p-4">
            {/* Tabbed Content Container */}
            <div className="flex-1 flex flex-col bg-white/50 backdrop-blur-sm rounded-lg border border-border/20 min-h-0">
              {/* Tab Headers */}
              <div className="flex border-b border-border/20 flex-shrink-0">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab('overview');
                  }}
                  className={cn(
                    "flex-1 px-4 py-2 text-xs font-medium transition-all",
                    activeTab === 'overview'
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  Overview
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveTab('script');
                  }}
                  className={cn(
                    "flex-1 px-4 py-2 text-xs font-medium transition-all",
                    activeTab === 'script'
                      ? "text-primary border-b-2 border-primary bg-primary/5"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                  )}
                >
                  Script
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto p-4 scrollbar-thin min-h-0">
                {activeTab === 'overview' && (
                  <div className="space-y-4">
                    {slide.metadata?.content_description && (
                      <div>
                        <h4 className="text-xs font-semibold text-foreground mb-2">
                          What This Scene Shows
                        </h4>
                        <p className="text-sm text-foreground/80 leading-relaxed">
                          {slide.metadata.content_description}
                        </p>
                      </div>
                    )}
                    {slide.metadata?.visual_instructions && (
                      <div>
                        <h4 className="text-xs font-semibold text-foreground mb-2">
                          Visual Direction
                        </h4>
                        <p className="text-sm text-foreground/80 leading-relaxed">
                          {slide.metadata.visual_instructions}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'script' && (
                  <div className="space-y-4">
                    {slide.metadata?.script_text ? (
                      <div>
                        <h4 className="text-xs font-semibold text-foreground mb-2">
                          Voice-Over Script
                        </h4>
                        <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-wrap">
                          {slide.metadata.script_text}
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground italic">
                        No script available for this scene.
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* GSAP indicator badge at bottom */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
              <span>GSAP Animation</span>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};
